import {
  OrganizationSwitchResponseV1Schema,
  type SuiteContextV1,
} from "@fulcrum5/suite-contracts";
import type { PlatformSessionContextValue } from "../session/PlatformSessionProvider.tsx";

export interface PlatformSuiteControls {
  readonly context: SuiteContextV1;
  switchOrganization(workosOrganizationId: string): Promise<void>;
  logout(): Promise<void>;
}

type PlatformSuiteSession = Pick<
  PlatformSessionContextValue,
  "api" | "identity" | "logout" | "phase" | "refresh" | "suiteContext"
>;

/**
 * Platform never rebuilds or augments the suite projection in the browser.
 * A null value is an explicit access-pending or unavailable state.
 */
export function suiteContextForPlatformSession(
  session: Pick<PlatformSuiteSession, "phase" | "suiteContext">,
): SuiteContextV1 | null {
  if (session.phase !== "ready" || session.suiteContext === null) return null;
  return session.suiteContext.currentProduct === "platform" ? session.suiteContext : null;
}

export function createPlatformSuiteControls(session: PlatformSuiteSession): PlatformSuiteControls | null {
  const context = suiteContextForPlatformSession(session);
  if (context === null) return null;

  return {
    context,
    async switchOrganization(workosOrganizationId: string): Promise<void> {
      const target = context.organizations.find((organization) => (
        organization.workosOrganizationId === workosOrganizationId
      ));
      if (!target) throw new Error("Organization is not available");
      await session.api.request({
        path: context.actions.switchOrganizationPath,
        method: "POST",
        body: { version: 1, workosOrganizationId: target.workosOrganizationId },
        responseSchema: OrganizationSwitchResponseV1Schema,
      });
      await session.refresh();
    },
    logout: () => session.logout(context.actions.logoutPath),
  };
}
